// Calls the API on the same origin (Vite forwards /api in development, nginx in production). Every change is sent as
// JSON, which the API requires.
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fields: Record<string, string> = {},
    public readonly details: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

type Options = { method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"; body?: unknown };

export async function api<T = unknown>(path: string, { method = "GET", body }: Options = {}): Promise<T> {
  const init: RequestInit = { method, credentials: "same-origin", headers: { Accept: "application/json" } };
  if (method !== "GET") {
    init.headers = { ...init.headers, "Content-Type": "application/json" };
    init.body = JSON.stringify(body ?? {});
  }
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, init);
  } catch {
    throw new ApiError(0, "NETWORK", "Can't reach the server. Check your connection and try again.");
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const e = data?.error ?? {};
    throw new ApiError(res.status, e.code ?? "ERROR", e.message ?? "Something went wrong.", e.details?.fields ?? {}, e.details ?? {});
  }
  return data as T;
}

export const APP_NAME: string = import.meta.env.VITE_APP_NAME || "TeamCollab";

export type Role = "owner" | "admin" | "member" | "guest";
export type Me = {
  user: {
    id: string; email: string; name: string; emailVerified: boolean; hasPassword: boolean; platformRole?: "super" | "support" | "billing" | null;
    avatarUrl: string | null; jobTitle: string; status: { emoji: string | null; text: string; expiresAt: string | null } | null; dndUntil: string | null;
    mfa: { enabled: boolean; enabledAt?: string; recoveryCodesLeft: number };
  };
  organizations: { id: string; name: string; slug: string; role: Role; suspended?: boolean }[];
};
